import type { ReactNode } from 'react'
import { CircleAlert, Inbox, LoaderCircle, LockKeyhole, ShieldAlert, Unplug } from 'lucide-react'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/utils'

export type StatePanelKind = 'loading' | 'empty' | 'error' | 'unavailable' | 'forbidden' | 'unauthorized'
export type StatePanelProps = {
  kind: StatePanelKind
  title?: ReactNode
  description?: ReactNode
  children?: ReactNode
  className?: string
}

const stateIcons = {
  loading: LoaderCircle,
  empty: Inbox,
  error: CircleAlert,
  unavailable: Unplug,
  forbidden: ShieldAlert,
  unauthorized: LockKeyhole,
}

export function StatePanel({ kind, title, description, children, className }: StatePanelProps) {
  const { locale, t } = useI18n()
  const zh = locale === 'zh-CN'
  const titles = {
    loading: t('loading'),
    empty: zh ? '暂无数据' : 'No data yet',
    error: zh ? '加载失败' : 'Unable to load',
    unavailable: zh ? '暂不可用' : 'Currently unavailable',
    forbidden: zh ? '没有访问权限' : 'Access denied',
    unauthorized: t('unauthorized'),
  }
  const Icon = stateIcons[kind]
  return (
    <section
      data-state={kind}
      role={kind === 'error' ? 'alert' : 'status'}
      aria-live={kind === 'error' ? 'assertive' : 'polite'}
      aria-busy={kind === 'loading'}
      className={cn('flex min-h-52 flex-col items-center justify-center gap-3 rounded-lg border bg-card px-6 py-10 text-center', className)}
    >
      <div className={cn('flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground', kind === 'error' && 'bg-destructive/10 text-destructive', kind === 'unavailable' && 'bg-warning/10 text-warning')}>
        <Icon aria-hidden='true' className={cn('size-5', kind === 'loading' && 'motion-safe:animate-spin')} />
      </div>
      <div className='max-w-xl space-y-1.5'>
        <h2 className='text-sm font-semibold'>{title ?? titles[kind]}</h2>
        {description && <p className='text-sm leading-6 text-muted-foreground'>{description}</p>}
      </div>
      {children && <div className='mt-1 flex flex-wrap justify-center gap-2'>{children}</div>}
    </section>
  )
}
